import EntityManage from '@common/components/EntityManage';
import StyledDivider from '@common/components/misc/StyledDivider';
import ApiService from '@common/services/ApiService';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { withStyles } from 'tss-react/mui';
import Banners from './Banners';
import Categories from './Categories';
import Divisions from './Divisions';
import Domains from './Domains';
import EmploymentTypes from './EmploymentTypes';
import ExternalCurrencyRequests from './ExternalCurrencyRequests';
import Features from './Features';
import { InstituteActions } from './InstituteActions';
import InstituteDetails from './InstituteDetails';
import Locations from './Locations';
import { Notification } from './Notification';
import Profiling from './Profiling';
import Programs from './Programs';
import { manageInstituteStyles } from './styles';
import UserGroups from './UserGroups';
import Vcm from './Vcm';
import VideoResources from './VideoResources';
import VocationalActivities from './VocationalActivities';

class ManageInstitute extends EntityManage {
  api = new ApiService();

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      dirty: false,
      errorMessage: null,
      instituteDetails: null,
      instituteLogo: null,
      tabValue: null,
      timezoneOptions: [],
      validationErrors: [],
      instituteSections: []
    };
  }

  componentDidMount() {
    this.getData();
  }

  getInstituteSections(instituteDetails) {
    const {
      user: { is_institute_admin, is_super_admin }
    } = this.props;

    if (!is_institute_admin && !is_super_admin) {
      return [];
    }

    const result = [
      ['institute_details', 'Institute Details'],      
    ];

    if (is_super_admin) {
      result.push(['features', 'Features']);
    }

    result.push(
      ['user_groups', 'User Groups'],
      ['locations', 'Locations'],
      ['divisions', 'Divisions'],
      ['external_currency_requests', 'External Currency'],
      ['programs', 'Programs'],
      ['categories', 'Program Categories'],
      ['domains', 'Domains'],
      ['profiling_tool_labels', 'Profiling'],
      ['employment_types', 'Employment Types, Passport Requirements & Enabled Features'],
      ['vocational_activities', 'Vocational Activities'],
      ['banners', 'Banners'],
      ['video_resources', 'Dashboard Resources'],
      ['notification', 'Notification']
    );

    if (instituteDetails?.vcm_enabled) {
      result.push(['vcm', 'VCM']);
    }

    return result;
  }

  convertTimezonesObject = (data) => {
    var timezoneOptions = {};
    var count = 0;
    Object.keys(data).map((obj) => {
      Object.assign(timezoneOptions, { [obj]: { id: obj, label: data[obj] } });
    });
    this.setState({ timezoneOptions: timezoneOptions });
  };

  getData = () => {
    if(this.props.institute.id == 0) {
      console.error('Institute ID is 0, cannot fetch institute data within ManageInstitue.js.');
      return;
    }
    const getInstitutePromise = this.api.get(
      'institutes',
      this.props.institute.id
    );
    const timezonesOptionsPromise = this.api.query('/api/v1/timezones/options');

    Promise.all([getInstitutePromise, timezonesOptionsPromise])
      .then(([res, timezonesOptions]) => {
        if (res.status === 200) {
          this.convertTimezonesObject(timezonesOptions.data);
          this.setEntity(res.json);
        } else {
          this.setState({
            errorMessage: 'Something went wrong. got status=' + res.status
          });
        }
      })
      .catch(this.processError);
  };

  handleInstituteChange = (newInstitute) => {
    this.setState({ dirty: true, instituteDetails: newInstitute });
  };

  handleLogoChange = (logo) => {
    let instituteDetails = { ...this.state.instituteDetails };
    instituteDetails.logo_filename = logo.name;
    this.setState({
      dirty: true,
      instituteDetails: instituteDetails,
      instituteLogo: logo
    });
  };

  handleTabChange = (event, tabValue) => {
    this.setState({ tabValue: tabValue });
    this.props.navigate(
      `/institutes/${this.props.institute.id}/${this.state.instituteSections[tabValue][0]}`
    );
  };

  makeTab = (section) => {
    const { classes } = this.props;

    return (
      <Tab
        key={section[0]}
        classes={{
          root: classes.defaultTabText,
          selected: classes.selectedTabText
        }}
        label={
          <Typography className={classes.typographyTabText}>
            {section[1]}
          </Typography>
        }
      />
    );
  };

  processResponse(res) {
    super.processResponse(res);
    if (res.status === 200) {
      if (this.state.instituteLogo != null) {
        this.uploadLogo();
      }
      // reload the page after changes saved
      window.location.reload();
    }
  }

  saveData = () => {
    this.api
      .saveResource('institute', {
        ...this.state.instituteDetails
      })
      .then((res) => {
        this.processResponse(res);
      })
      .catch((ex) => {
        this.processError(ex);
      });
  };

  selectTab = (instituteSection, instituteSections) => {
    var index = instituteSections.findIndex(
      (section) => instituteSection === section[0]
    );
    if (index < 0 || index >= instituteSections.length) {
      console.warn('Not valid institute section: ' + instituteSection);
      index = false;
    }
    return index;
  };

  setEntity = (json) => {
    const s = { validationErrors: [], dirty: false, errorMessage: null };
    const instituteSections = this.getInstituteSections(json.data);
    this.setState({
      ...s,
      instituteDetails: json.data,
      loading: false,
      instituteSections,
      tabValue: this.selectTab(
        this.props.section,
        instituteSections
      )
    });
  };

  uploadLogo = () => {
    let instituteDetails = { ...this.state.instituteDetails };
    instituteDetails.logo_filename = this.state.instituteLogo.name;
    this.setState({ instituteDetails: instituteDetails });

    const url = '/api/v1/institutes/' + this.props.institute.id + '/logo';
    this.api
      .upload(url, this.state.instituteLogo, 'institute[logo]')
      .then((response) => {
        this.getData();
      })
      .catch((ex) => console.error(ex));
  };

  render() {
    const {
      classes,
      user,
      section
    } = this.props;

    const {
      dirty,
      errorMessage,
      instituteDetails,
      tabValue,
      timezoneOptions,
      validationErrors
    } = this.state;

    const instituteDetailsTab = ['institute_details', 'features', 'notification', 'vcm', 'profiling_tool_labels', 'programs', 'external_currency_requests'].includes(
      section
    );

    const tabs = this.state.instituteSections.map((section) => {
      return this.makeTab(section);
    });

    const currentSection = this.state.instituteSections[tabValue]?.[0];

    if (this.state.loading) {
      return <CircularProgress />;
    }

    return (
      <Grid container className={classes.newInstituteContainer} spacing={2}>
        <Grid size={12}>
          {instituteDetails && (
            <InstituteActions
              dirty={dirty}
              errorMessage={errorMessage}
              instituteDetails={instituteDetails}
              instituteDetailsTab={instituteDetailsTab}
              onSave={this.saveData}
              styles={classes.createInstituteHeadline}
            />
          )}
        </Grid>
        <Grid size={12}>
          <Tabs
            onChange={this.handleTabChange}
            textColor="secondary"
            indicatorColor="secondary"
            value={tabValue}
            variant="scrollable"
          >
            {tabs}
          </Tabs>
          <StyledDivider />
          <React.Fragment />
        </Grid>
        <Grid size={12}>
          {instituteDetails && currentSection === 'institute_details' && (
            <InstituteDetails
              instituteDetails={instituteDetails}
              onInstituteChange={this.handleInstituteChange}
              onLogoChange={this.handleLogoChange}
              timezoneOptions={timezoneOptions}
              user={user}
              validationErrors={validationErrors}
            />
          )}
          {currentSection === 'user_groups' && <UserGroups />}
          {currentSection === 'features' && instituteDetails && (
            <Features
              instituteDetails={instituteDetails}
              onChange={this.handleInstituteChange}
            />
          )}
          {currentSection === 'locations' && <Locations />}
          {currentSection === 'divisions' && <Divisions />}
          {currentSection === 'external_currency_requests' && instituteDetails && (
            <ExternalCurrencyRequests
              instituteDetails={instituteDetails}
              onChange={this.handleInstituteChange}
              validationErrors={validationErrors}
            />
          )}
          {currentSection === 'programs' && instituteDetails && (
            <Programs
              instituteDetails={instituteDetails}
              onChange={this.handleInstituteChange}
              validationErrors={validationErrors}
            />
          )}
          {currentSection === 'categories' && <Categories />}
          {currentSection === 'domains' && <Domains />}
          {currentSection === 'profiling_tool_labels' && instituteDetails && (
            <Profiling
              instituteDetails={instituteDetails}
              onChange={this.handleInstituteChange}
              validationErrors={validationErrors}
            />
          )}
          {currentSection === 'employment_types' && (
            <EmploymentTypes instituteDetails={instituteDetails} />
          )}
          {currentSection === 'vocational_activities' && <VocationalActivities />}
          {currentSection === 'banners' && <Banners />}
          {currentSection === 'video_resources' && <VideoResources />}
          {currentSection === 'notification' && instituteDetails && (
            <Notification
              instituteDetails={instituteDetails}
              onChange={this.handleInstituteChange}
              validationErrors={validationErrors}
            />
          )}
          {currentSection === 'vcm' && instituteDetails && (
            <Vcm
              instituteDetails={instituteDetails}
              onChange={this.handleInstituteChange}
              validationErrors={validationErrors}
            />
          )}
        </Grid>
      </Grid>
    );
  }
}

function ManageInstituteWrapper(props) {
  const navigate = useNavigate();
  const params = useParams();
  return <ManageInstitute {...props} navigate={navigate} section={params.section} />;
}

export default withStyles(ManageInstituteWrapper, manageInstituteStyles, {
  withTheme: true
});
